// Dashboard API client for TakeCareAgencyDashboard
// Prefer env var; fall back to localhost in dev, production API in prod
const API_BASE_URL = process.env.REACT_APP_API_URL || (
  (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
    ? 'http://localhost:8000'
    : 'https://app.neurolonic.com/api'
);

class DashboardAPI {
  async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const token = (typeof localStorage !== 'undefined') ? localStorage.getItem('neuro_access_token') : null;
    const userId = (typeof localStorage !== 'undefined') ? localStorage.getItem('neuro_user_id') : null;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    // Attach user_id as query param when provided and endpoint is staff/patients list/create
    if (userId && (endpoint.startsWith('/dashboard/staff') || endpoint.startsWith('/dashboard/patients') || endpoint.startsWith('/dashboard/sessions') || endpoint.startsWith('/dashboard/emotion-details'))) {
      const hasQuery = url.includes('?');
      const scopedUrl = `${url}${hasQuery ? '&' : '?'}user_id=${encodeURIComponent(userId)}`;
      options = config; // keep reference name consistent below if needed
      return fetch(scopedUrl, config).then(async (response) => {
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || `HTTP ${response.status}`);
        return data;
      });
    }

    // Default: emit current user for downstream multi-tenancy
    if (userId && config.headers['X-User-Id'] === undefined) {
      config.headers = {
        ...config.headers,
        'X-User-Id': userId,
      };
    }

    try {
      const response = await fetch(url, config);
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || `HTTP ${response.status}`);
      }
      return data;
    } catch (error) {
      console.error(`API Error (${endpoint}):`, error);
      throw error;
    }
  }

  // Staff API
  async createStaff(staffData) {
    return this.request('/dashboard/staff', { method: 'POST', body: JSON.stringify(staffData) });
  }
  async updateStaff(staffId, staffData) {
    return this.request(`/dashboard/staff/${staffId}`, { method: 'PUT', body: JSON.stringify(staffData) });
  }
  async getAllStaff() {
    return this.request('/dashboard/staff');
  }
  async deleteStaff(staffId) {
    return this.request(`/dashboard/staff/${staffId}`, { method: 'DELETE' });
  }

  // School (students & staff) APIs
  async getSchoolStaff(createdBy) {
    const query = createdBy ? `?created_by=${encodeURIComponent(createdBy)}` : '';
    return this.request(`/school/staff${query}`);
  }

  async createSchoolStaff(staffData, createdBy) {
    const query = createdBy ? `?created_by=${encodeURIComponent(createdBy)}` : '';
    return this.request(`/school/staff${query}`, { method: 'POST', body: JSON.stringify(staffData) });
  }

  async getSchoolStudents(createdBy) {
    const query = createdBy ? `?created_by=${encodeURIComponent(createdBy)}` : '';
    return this.request(`/school/students${query}`);
  }

  // Patient API
  async createPatient(patientData) {
    return this.request('/dashboard/patients', { method: 'POST', body: JSON.stringify(patientData) });
  }
  async updatePatient(patientId, patientData) {
    return this.request(`/dashboard/patients/${patientId}`, { method: 'PUT', body: JSON.stringify(patientData) });
  }
  async getAllPatients() {
    return this.request('/dashboard/patients');
  }
  async deletePatient(patientId) {
    return this.request(`/dashboard/patients/${patientId}`, { method: 'DELETE' });
  }

  // Session API
  async createSession(sessionData) {
    return this.request('/dashboard/sessions', { method: 'POST', body: JSON.stringify(sessionData) });
  }
  async getPatientSessions(patientId) {
    return this.request(`/dashboard/patients/${patientId}/sessions`);
  }

  // Emotion Details API
  async createEmotionDetails(emotionData) {
    return this.request('/dashboard/emotion-details', { method: 'POST', body: JSON.stringify(emotionData) });
  }

  // Session Notes API
  async createSessionNote(noteData) {
    return this.request('/dashboard/session-notes', { method: 'POST', body: JSON.stringify(noteData) });
  }
  async getSessionNotes(sessionId) {
    return this.request(`/dashboard/sessions/${sessionId}/notes`);
  }

  async getDashboardReport(userId, type) {
    const today = new Date().toISOString().split('T')[0];
    return this.request('/dashboard', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        current_date: today,
        report_type: (type || 'weekly').toLowerCase()
      })
    });
  }

  async getSchoolEmotionSummary(entityType, entityId, range = 'weekly') {
    const safeRange = (range || 'weekly').toLowerCase();
    const path = entityType === 'staff'
      ? `/school/staff/${entityId}/emotions/summary?range=${encodeURIComponent(safeRange)}`
      : `/school/students/${entityId}/emotions/summary?range=${encodeURIComponent(safeRange)}`;
    return this.request(path);
  }

  async getSchoolEmotionHistory(entityType, entityId) {
    const path = entityType === 'staff'
      ? `/school/staff/${entityId}/emotions`
      : `/school/students/${entityId}/emotions`;
    return this.request(path);
  }
}

const dashboardApi = new DashboardAPI();
export default dashboardApi;


